/**
 * @file Submission UI — A1A3DetailsReadonlySection.
 * Combined A1–A3 summary on Dataset Overview — same UI as contributor A1A3 stage.
 */

import React, { useMemo } from "react";
import "../../pages/Dashboard/Contributor/Stages/A1-A3/A1A3.css";
import "./LifecycleStageDetailsReadonly.css";
import {
  buildA1A3CarbonUnitOptions,
  buildA1A3EnergyUnitOptions,
  computedEmbodiedCarbonPerFinished,
  computedEmbodiedEnergyPerFinished,
  readBackgroundFinishedDeclaredQty,
  resolveBackgroundFinishedMaterialUnitSymbol,
  unitIdToSymbolFromReferenceLabels,
} from "../../pages/Dashboard/Contributor/Stages/A1-A3/a1a3SummaryFromSubmission";
import DatasetOverviewFormShell from "./datasetOverview/DatasetOverviewFormShell";
import type { AnyObj } from "./readonlyFormDisplay";
import { EMBODIED_CARBON_PER_FUNCTIONAL_UNIT_LABEL } from "../../pages/Dashboard/Contributor/Stages/shared/contributorFieldDisplayLabels";

/** Type definition — A1A3Details Readonly Section Props. */
export type A1A3DetailsReadonlySectionProps = {
  submission: AnyObj;
};

function formatNum(n: number | null | undefined, digits = 3): string {
  if (n == null || !Number.isFinite(n)) return "";
  return String(Math.round(n * 10 ** digits) / 10 ** digits);
}

const A1A3DetailsReadonlySection: React.FC<A1A3DetailsReadonlySectionProps> = ({
  submission,
}) => {
  const summary = (submission.a1a3Summary ?? {}) as AnyObj;

  const energyBase = useMemo(() => {
    const saved = String(summary.embodiedEnergyValue ?? "").trim();
    if (saved) return saved;
    const per = computedEmbodiedEnergyPerFinished(submission);
    if (per != null) return formatNum(per);
    return "";
  }, [submission, summary]);

  const carbonBase = useMemo(() => {
    const saved = String(summary.embodiedCarbonValue ?? "").trim();
    if (saved) return saved;
    const per = computedEmbodiedCarbonPerFinished(submission);
    if (per != null) return formatNum(per);
    return "";
  }, [submission, summary]);

  const unitIdToSymbol = useMemo(
    () => unitIdToSymbolFromReferenceLabels(submission),
    [submission]
  );

  const denominatorSymbol = useMemo(
    () => resolveBackgroundFinishedMaterialUnitSymbol(submission, unitIdToSymbol),
    [submission, unitIdToSymbol]
  );

  const energyUnitOptions = useMemo(
    () => buildA1A3EnergyUnitOptions(denominatorSymbol),
    [denominatorSymbol]
  );

  const carbonUnitOptions = useMemo(
    () => buildA1A3CarbonUnitOptions(denominatorSymbol),
    [denominatorSymbol]
  );

  const energyUnit = useMemo(() => {
    const saved = String(summary.embodiedEnergyUnit ?? "").trim();
    if (saved && energyUnitOptions.some((o) => o.value === saved)) return saved;
    return energyUnitOptions[0]?.value ?? "MJ";
  }, [summary, energyUnitOptions]);

  const carbonUnit = useMemo(() => {
    const saved = String(summary.embodiedCarbonUnit ?? "").trim();
    if (saved && carbonUnitOptions.some((o) => o.value === saved)) return saved;
    return carbonUnitOptions[0]?.value ?? "kg CO₂e";
  }, [summary, carbonUnitOptions]);

  const hasQty = readBackgroundFinishedDeclaredQty(submission) != null;

  return (
    <DatasetOverviewFormShell variant="a1a3" bare>
      <div className="a-section">
        <h3 className="a-section-title">Embodied Energy (A1–A3)</h3>
        <div className="a1a3-value-unit-stack">
          <div className="a1a3-value-unit-labels">
            <div className="a1a3-label-cell">
              <label htmlFor="a1a3-ro-energy" className="a1a3-value-field-label">
                <span>Value</span>
              </label>
            </div>
            <div className="a1a3-label-cell">
              <label htmlFor="a1a3-ro-energy-unit" className="a1a3-unit-label">
                Unit
              </label>
            </div>
          </div>
          <div className="a1a3-value-unit-inputs">
            <div className="a-field">
              <input
                id="a1a3-ro-energy"
                type="text"
                readOnly
                disabled
                value={energyBase}
              />
            </div>
            <div className="a-field">
              <select
                id="a1a3-ro-energy-unit"
                className="a1a3-unit-select"
                disabled
                value={energyUnit}
              >
                {energyUnitOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        {hasQty && (
          <p className="a1a3-readonly-hint">
            Per finished material quantity
            {denominatorSymbol ? ` (${denominatorSymbol})` : ""}.
          </p>
        )}
      </div>

      <div className="a-section">
        <h3 className="a-section-title">Embodied Carbon (A1–A3)</h3>
        <div className="a1a3-value-unit-stack">
          <div className="a1a3-value-unit-labels">
            <div className="a1a3-label-cell">
              <label htmlFor="a1a3-ro-carbon" className="a1a3-value-field-label">
                <span>{EMBODIED_CARBON_PER_FUNCTIONAL_UNIT_LABEL}</span>
              </label>
            </div>
            <div className="a1a3-label-cell">
              <label htmlFor="a1a3-ro-carbon-unit" className="a1a3-unit-label">
                Unit
              </label>
            </div>
          </div>
          <div className="a1a3-value-unit-inputs">
            <div className="a-field">
              <input
                id="a1a3-ro-carbon"
                type="text"
                readOnly
                disabled
                value={carbonBase}
              />
            </div>
            <div className="a-field">
              <select
                id="a1a3-ro-carbon-unit"
                className="a1a3-unit-select"
                disabled
                value={carbonUnit}
              >
                {carbonUnitOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    </DatasetOverviewFormShell>
  );
};

/** React component — A1A3Details Readonly Section. */
export default A1A3DetailsReadonlySection;
